import React, { useEffect, useMemo, useState } from 'react';
import {
    TextField,
    DatePicker,
    Picker,
    Item,
    Flex
} from '@adobe/react-spectrum';
import { parseDate, CalendarDate } from '@internationalized/date';
import { useDependency } from '../../../../../contexts/DependencyProvider';
import { GetCodebookRequest } from '../../../../../services/soap/item/requests/GetCodebookRequest';

type FieldType = '1' | '2' | '3' | '4' | '5';

interface FieldConfig {
    [key: string]: string;
}

interface ValuesData {
    [key: string]: any;
}

interface DynamicProductTypeFieldsProps {
    config?: any | null;
    data?: any | null;
    onChange: (newData: any) => void;
}

type FieldValue = string | CalendarDate | null;
type SelectOption = { id: string; name: string; priority?: number };

const fieldTypeComponents: {
    [key in FieldType]: (
        key: string,
        label: string,
        value: FieldValue,
        onChange: (fieldKey: string, value: FieldValue) => void,
        options?: SelectOption[]
    ) => JSX.Element;
} = {
    '1': (key, label, value, onChange) => (
        <TextField
            key={key}
            label={label}
            value={(value as string) || ''}
            onChange={(val) => onChange(key, val)}
        />
    ),
    '2': (key, label, value, onChange) => (
        <TextField
            key={key}
            label={label}
            type="number"
            value={(value as string) || ''}
            onChange={(val) => onChange(key, val)}
        />
    ),
    '3': (key, label, value, onChange) => (
        <TextField
            key={key}
            label={label}
            type="number"
            value={(value as string) || ''}
            onChange={(val) => onChange(key, val)}
        />
    ),
    '4': (key, label, value, onChange) => (
        <DatePicker
            key={key}
            label={label}
            value={value as CalendarDate | null}
            onChange={(val) => onChange(key, val)}
        />
    ),
    '5': (key, label, value, onChange, options = []) => (
        <Picker
            key={key}
            label={label}
            selectedKey={(value as string) || undefined}
            onSelectionChange={(val) => onChange(key, val as string)}
        >
            {options.map((opt) => (
                <Item key={opt.id}>{opt.name}</Item>
            ))}
        </Picker>
    )
};

const DynamicProductTypeFields: React.FC<DynamicProductTypeFieldsProps> = ({
    config = {},
    data = {},
    onChange
}) => {
    const { store, itemService } = useDependency();
    const [codebookMap, setCodebookMap] = useState<Record<string, SelectOption[]>>({});

    function normalizeDateString(value: string): string | null {
        const parts = value.substring(0, 10).split('-');

        if (parts.length !== 3) return null;

        const [a, b, c] = parts;

        // Case 1: YYYY-MM-DD
        if (a.length === 4) {
            return `${a}-${b}-${c}`;
        }

        // Case 2: MM-DD-YYYY
        if (c.length === 4 && parseInt(a) <= 12) {
            return `${c}-${a}-${b}`;
        }

        // Case 3: DD-MM-YYYY
        if (c.length === 4 && parseInt(b) <= 12) {
            return `${c}-${b}-${a}`;
        }

        return null;
    }

    useEffect(() => {
        let isCancelled = false;

        const fetchCodebooks = async () => {
            const listFieldCodebookIds = Array.from(
                new Set(
                    Array.from({ length: 10 }, (_, i) => i)
                        .map((i) => {
                            const key = `field${i}`;
                            return config?.[`${key}_type`] === '5' ? String(config?.[`${key}_values`] || '').trim() : '';
                        })
                        .filter(Boolean)
                )
            );

            if (listFieldCodebookIds.length === 0) {
                setCodebookMap({});
                return;
            }

            const entries = await Promise.all(
                listFieldCodebookIds.map(async (codebookId) => {
                    try {
                        const res = await itemService.getCodebook(
                            new GetCodebookRequest(store.Server, store.SessionId, codebookId)
                        );

                        const options = (res?.CODEBOOK?.[0]?.VALUES || [])
                            .map((item: any) => ({
                                id: String(item?.id ?? ''),
                                name: String(item?.name ?? ''),
                                priority: Number(item?.priority ?? 0),
                            }))
                            .filter((item: SelectOption) => item.id && item.name)
                            .sort((a: SelectOption, b: SelectOption) => Number(a.priority || 0) - Number(b.priority || 0));

                        return [codebookId, options] as const;
                    } catch (error) {
                        console.error('Error loading product field codebook:', error);
                        return [codebookId, []] as const;
                    }
                })
            );

            if (!isCancelled) {
                setCodebookMap(Object.fromEntries(entries));
            }
        };

        fetchCodebooks();

        return () => {
            isCancelled = true;
        };
    }, [config, itemService, store.Server, store.SessionId]);

    const normalizedConfig = useMemo(() => config || {}, [config]);

    const renderFields = () => {
        const fields: JSX.Element[] = [];

        for (let i = 0; i < 10; i++) {
            const key = `field${i}`;
            const type = normalizedConfig?.[`${key}_type`] as FieldType | undefined;

            if (!type || !(type in fieldTypeComponents)) continue;

            const label = normalizedConfig?.[`${key}_name`] || key;
            const codebookId = String(normalizedConfig?.[`${key}_values`] || '').trim();
            const options = type === '5' && codebookId ? (codebookMap[codebookId] || []) : [];

            let value: FieldValue = data?.[key];

            if (type === '4') {
                // Date parsing
                if (typeof value === 'string') {
                    try {
                        const normalized = normalizeDateString(value);
                        if (normalized) {
                            value = parseDate(normalized);
                        }
                    } catch {
                        value = null;
                    }
                }
            } else if (type === '5') {
                const selectedId = String(data?.[`${key}_id`] || '').trim();
                const selectedText = String(data?.[key] || '').trim();

                const matchedOption = options.find((opt) =>
                    opt.id === selectedId || opt.name === selectedText
                );

                value = matchedOption?.id || selectedId || '';
            }

            const renderer = fieldTypeComponents[type];
            if (renderer) {
                fields.push(
                    renderer(
                        key,
                        label,
                        value,
                        (fieldKey, val) => {
                            if (type === '5') {
                                const selectedId = String(val || '');
                                const selected = options.find((opt) => opt.id === selectedId);
                                onChange({
                                    ...data,
                                    [fieldKey]: selected?.name || '',
                                    [`${fieldKey}_id`]: selectedId,
                                });
                                return;
                            }

                            onChange({ ...data, [fieldKey]: val });
                        },
                        options
                    )
                );
            }
        }

        return fields;
    };

    return (
        <Flex direction="row" gap="size-100" wrap>
            {renderFields()}
        </Flex>
    );
};

export default DynamicProductTypeFields;
